import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import SEO from "../components/SEO";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeownerNavBar from "../components/Navbar/HomeownerNavBar";
import Footer from "../components/HomePage/Footer";
import HeroCarousel from "../components/HomePage/HeroCarousel";
import HomeReferralRibbon from "../components/HomePage/HomeReferralRibbon";
import ServiceHighlights from "../components/HomePage/ServiceHighlights";
import BrandCarousel from "../components/HomePage/BrandCarousel";
import WhyChooseUs from "../components/HomePage/WhyChooseUs";
import HowItWorks from "../components/HomePage/HowItWorks";
import CTASection from "../components/HomePage/CTASection";
import NavbarCTAButton from "../components/Button/NavbarCTAButton";
import WhatsAppButton from "../components/HomePage/WhatsAppButton";
import useScrollPosition from "../hooks/useScrollPosition";
import PageTransition from "../components/layouts/PageTransition";
import { Calendar, ShieldCheck } from "lucide-react";
import { ONETIME_FROM_RATE_LABEL } from "../constants/pricing";

const MotionBox = motion.create(Box);
const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "HomeHelpy",
  "description": "Professional home cleaning and aircon servicing in Singapore. Book online with transparent pricing and flexible scheduling.",
  "url": "https://www.homehelpy.com",
  "telephone": "+6589388171",
  "address": { "@type": "PostalAddress", "addressCountry": "SG" },
  "areaServed": "Singapore",
  "priceRange": "$$",
  "image": "https://www.homehelpy.com/home/carou1.webp",
};

export default function HomePage() {
  const { scrollPosition } = useScrollPosition();
  const navigate = useNavigate();
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const hero = document.getElementById("home-hero");
    if (!hero) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting;
        setShowSticky(visible);
        window.dispatchEvent(
          new CustomEvent("sticky-cta-change", { detail: { visible } }),
        );
      },
      { threshold: 0 },
    );
    observer.observe(hero);
    return () => {
      observer.disconnect();
      window.dispatchEvent(
        new CustomEvent("sticky-cta-change", { detail: { visible: false } }),
      );
    };
  }, []);

  return (
    <PageTransition>
      <Flex
        position="relative"
        bg="white"
        direction="column"
        w="100%"
        minH="100vh"
        alignItems="center"
      >
        <SEO
          title="Home Cleaning &amp; Aircon Servicing in Singapore"
          description="Book professional home cleaning and aircon servicing online. Transparent pricing, trained helpers, and flexible scheduling across Singapore."
          canonical="/"
          schema={localBusinessSchema}
        />
        <WhatsAppButton />
        {/* Sticky mobile CTA */}
        <AnimatePresence>
          {showSticky && (
            <MotionBox
              key="home-sticky-cta"
              id="sticky-cta-bar"
              position="fixed"
              left="0"
              right="0"
              bottom="0"
              zIndex={50}
              display={{ base: "flex", tablet: "none" }}
              alignItems="center"
              justifyContent="space-between"
              gap="0.75rem"
              bg="rgba(255,255,255,0.96)"
              backdropFilter="blur(14px)"
              borderTop="1px solid"
              borderColor="blackAlpha.100"
              borderTopLeftRadius="1rem"
              borderTopRightRadius="1rem"
              px="1rem"
              py="0.75rem"
              boxShadow="0 -10px 30px rgba(15,23,42,0.08)"
              initial={{ y: "100%" }}
              animate={{ y: 0, transition: { duration: 0.35, ease } }}
              exit={{ y: "100%", transition: { duration: 0.25, ease } }}
            >
              <Flex
                align="center"
                gap="0.5rem"
                flex="1"
                minW={0}
                overflow="hidden"
              >
                <Icon
                  as={ShieldCheck}
                  boxSize={6}
                  color="primary.surface.default"
                  flexShrink={0}
                />
                <Box minW={0}>
                  <Text
                    fontWeight="400"
                    fontSize="0.75rem"
                    color="greyscale.text.body"
                    lineHeight="1.3"
                  >
                    Cleaning Services
                  </Text>
                  <Text
                    fontWeight="700"
                    fontSize="0.88rem"
                    color="greyscale.text.title"
                    lineHeight="1.3"
                  >
                    From {ONETIME_FROM_RATE_LABEL}
                  </Text>
                </Box>
              </Flex>
              <NavbarCTAButton
                scrolled
                onClick={() => navigate("/dashboard")}
                icon={<Calendar size={16} />}
                h="2.7rem"
                px="1.5rem"
                minW="8.5rem"
                flexShrink={0}
              >
                Book Now
              </NavbarCTAButton>
            </MotionBox>
          )}
        </AnimatePresence>

        <HomeownerNavBar
          heroMode={scrollPosition === 0}
          position="fixed"
          top={0}
        />

        <Flex
          direction="column"
          w="100%"
          maxW="1512px"
          pt="0"
          px={{ base: "0", tablet: "1.5rem", desktop: "2.5rem" }}
          gap={{ base: "4rem", tablet: "5rem", desktop: "6rem" }}
        >
          <HeroCarousel />
          <Box
            mx={{ base: "0", tablet: "-1.5rem", desktop: "-2.5rem" }}
            mt={{ base: "-4rem", tablet: "-5rem", desktop: "-6rem" }}
          >
            <HomeReferralRibbon />
            <ServiceHighlights />
            <HowItWorks />
            <WhyChooseUs />
            <BrandCarousel />
            <CTASection />
          </Box>
        </Flex>
        <Footer />
      </Flex>
    </PageTransition>
  );
}
